import {
  Allergens,
  DayOfWeek, DietGoal, FoodPreference, Gender, HandledError, Meal, MealIngredient,
  MealOfDay, MealPlanData, MealPlanWithMeals,
  MealPreferences, MealsOfDayInOrder, MetricToImperialConverter, NutritionRating,
  ProgramType,
  ShoppingItem,
  ShoppingListExclusion,
  TimeSpan,
  UserMealPlan,
} from 'fitbeat.models';
import moment from 'moment';
import { NutritionConfig } from '../../config';
import { UserProfile } from '../../entities';
import {
  DbGymRepository,
  IMealIngredientsRepository, IMealPlanRepository, IMealRepository,
  IUserProfileRepository,
} from '../../persistence';
import { NutritionSerializer } from '../../persistence/serialization/nutrition';
import { EnabledFeaturesHydrationService } from '../user/EnabledFeaturesHydrationService';
import { INutrition, INutritionAdmin } from './inutrition';

function roundFloatToDecimalPlaces(f: number, decimalPlaces: number): number {
  return parseFloat(f.toFixed(decimalPlaces));
}

export class Nutrition implements INutrition, INutritionAdmin {
  public static disableImperialOnMacros = true;

  public static getTimeOfMealName(nextMeal: any, timeOfDay: any): MealOfDay {
    let timeOfDayMeal = nextMeal.timeOfMeal.find((x: string) => x === timeOfDay);
    if (!timeOfDayMeal) {
      switch (timeOfDay) {
        case MealOfDay.MorningSnack:
          timeOfDayMeal = MealOfDay.MorningSnack;
          break;
        case MealOfDay.AfternoonSnack:
          timeOfDayMeal = MealOfDay.AfternoonSnack;
          break;
        default:
          timeOfDayMeal = MealOfDay.Snack;
          break;
      }
    }
    return timeOfDayMeal;
  }

  public static setHourOnDate(date: moment.Moment, mealOfDay: MealOfDay) {
    const hour = NutritionConfig.MealCutOffTimeMap.get(mealOfDay);
    return date.clone().set({ h: hour, m: 0, s: 0 }).utc(true);
  }

  public static getMealTimeOfDay(date: moment.Moment) {
    const currentDate = date.clone().utc(true);
    const start = currentDate.clone().set({ h: 0, m: 0, s: 0 }).utc(true);
    const breakfastCutoff = this.setHourOnDate(currentDate, MealOfDay.Breakfast);
    const morningSnackCutoff = this.setHourOnDate(currentDate, MealOfDay.MorningSnack);
    const lunchCutoff = this.setHourOnDate(currentDate, MealOfDay.Lunch);
    const afternoonSnackCutoff = this.setHourOnDate(currentDate, MealOfDay.AfternoonSnack);
    const dinnerCutoff = this.setHourOnDate(currentDate, MealOfDay.Dinner);
    const end = currentDate.clone().set({ h: 23, m: 59, s: 59 }).utc(true);
    let timeOfDay = MealOfDay.Snack;

    if (currentDate.isBetween(start, breakfastCutoff)) {
      timeOfDay = MealOfDay.Breakfast;
    }
    if (currentDate.isBetween(breakfastCutoff, morningSnackCutoff)) {
      timeOfDay = MealOfDay.MorningSnack;
    }
    if (currentDate.isBetween(morningSnackCutoff, lunchCutoff)) {
      timeOfDay = MealOfDay.Lunch;
    }
    if (currentDate.isBetween(lunchCutoff, afternoonSnackCutoff)) {
      timeOfDay = MealOfDay.AfternoonSnack;
    }
    if (currentDate.isBetween(afternoonSnackCutoff, dinnerCutoff)) {
      timeOfDay = MealOfDay.Dinner;
    }
    if (currentDate.isBetween(dinnerCutoff, end)) {
      timeOfDay = MealOfDay.BreakfastNextDay;
    }

    return timeOfDay;
  }

  public static findNextXMeals(
    numberOfMeals: number,
    mealPlan: MealPlanWithMeals,
    requireRating: boolean,
    currentDateMoment: moment.Moment,
  ) {
    const nextMeals: any = [];
    const searchForMealsFromTomorrow = (dateFor: moment.Moment) => {
      const tomorrowMorningMoment = moment(dateFor).add(1, 'day');
      tomorrowMorningMoment.hour(6);
      findNextMeals(tomorrowMorningMoment);
    };

    const findNextMeals = (dateFor: moment.Moment) => {
      if (nextMeals.length >= numberOfMeals) {
        return;
      }

      const timeOfDay = this.getMealTimeOfDay(dateFor);
      if (timeOfDay === MealOfDay.BreakfastNextDay) {
        searchForMealsFromTomorrow(dateFor);
        return;
      }

      const currentMealIndex = MealsOfDayInOrder.findIndex((mealOfDay) => mealOfDay === timeOfDay);
      for (let i = currentMealIndex; i < MealsOfDayInOrder.length; i++) {
        const mealOfDay = MealsOfDayInOrder[i];
        const dayOfWeek = dateFor.format('dddd');
        const mealSchedule = mealPlan.mealSchedule[dayOfWeek];
        const nextMeal = mealSchedule[mealOfDay];
        if (nextMeal) {
          const timeOfMealName = this.getTimeOfMealName(nextMeal, mealOfDay);
          nextMeals.push({
            ...nextMeal,
            timeOfMeal: [timeOfMealName],
            timeOfMealName,
            requireRating,
            dayOfWeek,
            date: dateFor,
          });
        }
      }

      const haveFoundEnoughMeals = nextMeals.length >= numberOfMeals;
      if (!haveFoundEnoughMeals) {
        searchForMealsFromTomorrow(dateFor);
      }
    };

    findNextMeals(currentDateMoment);
    return nextMeals.slice(0, numberOfMeals);
  }

  constructor(
    private userProfileRepository: IUserProfileRepository,
    private mealRepository: IMealRepository,
    private mealPlanRepository: IMealPlanRepository,
    private mealIngredientRepository: IMealIngredientsRepository,
  ) { }

  public async saveLongTermGoal(userId: string, programType: ProgramType): Promise<UserProfile> {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }

    userProfile.longTermGoal = programType;
    return this.userProfileRepository.update(userProfile);
  }

  public async saveUnitPreference(userId: string, useImperialUnit: boolean): Promise<UserProfile> {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }
    if (!(typeof useImperialUnit === 'boolean')) {
      throw new HandledError('Invalid argument type');
    }
    userProfile.useImperialUnit = useImperialUnit;
    return this.userProfileRepository.update(userProfile);
  }

  public async saveFoodPreferences(
    userId: string,
    foodPreference: FoodPreference,
    useImperialUnit: boolean,
    dietGoal: DietGoal,
    gender: Gender,
  ): Promise<UserProfile> {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }
    if (!userProfile.mealPreferences) {
      userProfile.mealPreferences = new MealPreferences();
    }
    userProfile.mealPreferences.foodPreference = foodPreference || userProfile.mealPreferences.foodPreference;
    userProfile.useImperialUnit = useImperialUnit || false;
    userProfile.mealPreferences.dietGoal = dietGoal || userProfile.mealPreferences.dietGoal;
    userProfile.gender = gender || userProfile.gender;
    return this.userProfileRepository.update(userProfile);
  }

  public async saveDietGoal(userId: string, dietGoal: DietGoal, gender: Gender): Promise<UserProfile> {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }
    if (!userProfile.mealPreferences) {
      userProfile.mealPreferences = new MealPreferences();
      userProfile.mealPreferences.foodPreference = FoodPreference.AllFoodGroups;
    }
    userProfile.mealPreferences.dietGoal = dietGoal;
    userProfile.gender = gender;
    return this.userProfileRepository.update(userProfile);
  }

  public async getMealPlanForSavedPreferences(userId: string): Promise<MealPlanWithMeals> {
    const mealPlanWithMeals = await this.getMealPlanForSavedPreferencesNonSerialised(userId);
    return NutritionSerializer.serializeMealPlanWithMeals(mealPlanWithMeals);
  }

  public async setShoppingListSettings(
    userId: string,
    allShoppingListExclusions: ShoppingListExclusion[],
    servingCount = 1,
  ): Promise<void> {
    const userProfile = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }

    const userMealPlansForUser = await this.mealPlanRepository.getUserMealPlansForUser(
      userProfile,
      this.getDietGoalFor(userProfile),
    );

    if (!userMealPlansForUser) {
      throw new HandledError(`No meal plan found for user ${userId}`);
    }

    userMealPlansForUser.shoppingListExclusions = allShoppingListExclusions;
    userMealPlansForUser.shoppingListServingCount = servingCount;
    await this.createOrUpdateMealPlan(userMealPlansForUser);
  }

  public async setShoppingListChecklistIngredients(userId: string, checklistIngredientIds: string[]): Promise<void> {
    const userProfile = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }

    const userMealPlansForUser = await this.mealPlanRepository.getUserMealPlansForUser(
      userProfile,
      this.getDietGoalFor(userProfile),
    );

    if (!userMealPlansForUser) {
      throw new HandledError(`No meal plan found for user ${userId}`);
    }

    userMealPlansForUser.checklistIngredientIds = checklistIngredientIds;
    await this.createOrUpdateMealPlan(userMealPlansForUser);
  }

  public async getMealBase64Image(mealId: string): Promise<string | undefined> {
    return this.mealRepository.getImage(mealId);
  }

  public async setMealBase64Image(mealId: string, base64Image: string): Promise<boolean> {
    return this.mealRepository.setImage(mealId, base64Image);
  }

  public async createOrUpdateMealPlan(mealPlanData: MealPlanData): Promise<MealPlanData> {
    const queryId = mealPlanData._id || '';
    const possibleMealPlan: MealPlanData | undefined = await this.mealPlanRepository.get(queryId);
    if (!possibleMealPlan) {
      mealPlanData._id = MealPlanData.GenerateId(mealPlanData.displayName);
      mealPlanData.mealIdSchedule = new Map<DayOfWeek, Map<MealOfDay, string>>();
      mealPlanData.shoppingListExclusions = [];
      mealPlanData = await this.mealPlanRepository.add(mealPlanData);
    } else {
      mealPlanData = await this.mealPlanRepository.update(mealPlanData);
    }
    return mealPlanData;
  }

  public async createOrUpdateMeal(meal: Meal): Promise<Meal> {
    meal._id = meal._id ? meal._id : Meal.GenerateId(meal.displayName, meal.mealPlanId);
    return this.mealRepository.update(meal);
  }

  public async linkMealToPlan(
    mealId: string,
    mealPlanId: string,
    dayOfWeek: string,
    mealOfDay: string,
  ): Promise<MealPlanData> {
    const mealPlanData: MealPlanData | undefined = await this.mealPlanRepository.get(mealPlanId);
    if (!mealPlanData) {
      throw new HandledError(`No meal plan found with _id ${mealPlanId}`);
    }

    const meal: Meal | undefined = await this.mealRepository.get(mealId);
    if (!meal) {
      throw new HandledError(`No meal found with _id ${mealId}`);
    }
    if (meal.timeOfMeal) {
      const timeOfMeal = [...meal.timeOfMeal, MealOfDay[mealOfDay]];
      meal.timeOfMeal = [...new Set(timeOfMeal)];
    } else {
      meal.timeOfMeal = [MealOfDay[mealOfDay]];
    }
    if (meal.foodPreference) {
      const foodPreference = [...meal.foodPreference, mealPlanData.foodPreference];
      meal.foodPreference = [...new Set(foodPreference)];
    } else {
      meal.foodPreference = [mealPlanData.foodPreference];
    }

    meal.dietGoal = mealPlanData.dietGoal;
    meal.gender = mealPlanData.gender;

    let mealPlanForDay: Map<MealOfDay, string> | undefined = mealPlanData.mealIdSchedule.get(DayOfWeek[dayOfWeek]);
    if (mealPlanForDay) {
      mealPlanForDay.set(MealOfDay[mealOfDay], mealId);
    } else {
      mealPlanForDay = new Map([[MealOfDay[mealOfDay], mealId]]);
    }
    mealPlanData.mealIdSchedule.set(DayOfWeek[dayOfWeek], mealPlanForDay);
    await this.mealRepository.update(meal);
    return this.mealPlanRepository.update(mealPlanData);
  }

  public async getBase64Thumbnail(mealId: string): Promise<string | undefined> {
    return this.mealRepository.getThumbnail(mealId);
  }

  public async setMealBase64Thumbnail(mealId: string, base64Image: string): Promise<boolean> {
    return this.mealRepository.setThumbnail(mealId, base64Image);
  }

  public async getMealsForMealPlan(mealPlanId: string): Promise<Meal[]> {
    return this.mealRepository.getMealsForMealPlan(mealPlanId);
  }

  public async getMeal(mealId: string): Promise<Meal | undefined> {
    return this.mealRepository.get(mealId);
  }

  public async getMealPlan(mealPlanid: string): Promise<MealPlanData | undefined> {
    const mealPlanData = await this.mealPlanRepository.get(mealPlanid);
    if (mealPlanData) {
      return NutritionSerializer.serializeMealPlanData(mealPlanData);
    }

    return undefined;
  }

  public async getThumbnails(mealPlanId: string): Promise<any> {
    return this.mealRepository.getThumbnails(mealPlanId);
  }

  public async isMealPrefSet(userId: string): Promise<boolean> {
    const userProfile = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }
    const { mealPreferences } = userProfile;
    return typeof mealPreferences !== 'undefined';
  }

  // Deprecated user getNextXMeals
  public async getNextMeal(userId: string, date: Date, timeZone: string): Promise<any | undefined> {
    const nextMeals = await this.getNextXMeals(
      userId,
      1,
      date,
      timeZone,
    );
    return nextMeals[0];
  }

  public async getNextXMeals(
    userId: string,
    numberOfMeals: number,
    date: Date,
    timeZone: string,
  ): Promise<any | undefined> {
    const dateInTimeZone = TimeSpan.getDateAndTimeInTimezone(date, timeZone);
    const currentDateMoment = moment(dateInTimeZone);
    const [mealPlan, requireRating] = await Promise.all([
      this.getMealPlanForSavedPreferences(userId),
      this.checkIfRatingRequired(userId, currentDateMoment),
    ]);

    return Nutrition.findNextXMeals(numberOfMeals, mealPlan, requireRating, currentDateMoment);
  }

  public async getMealsForTimeOfDay(timeOfDay: string, mealPlanId: string, userId: string): Promise<Meal[]> {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    const userAllergens = userProfile?.allergens ?? [];
    const mealPlanData: MealPlanData | undefined = await this.mealPlanRepository.get(mealPlanId);
    if (!mealPlanData) {
      throw new HandledError(`No meal plan found with _id ${mealPlanId}`);
    }
    const organisationMealTag = this.getAdditionalMealsTag(userProfile);
    let meals: Meal[];
    if (MealOfDay[timeOfDay] === MealOfDay.MorningSnack || MealOfDay[timeOfDay] === MealOfDay.AfternoonSnack) {
      meals = await this.mealRepository.getAllMealsForTimeOfDay(
        MealOfDay.Snack,
        mealPlanData.gender,
        mealPlanData.dietGoal,
        mealPlanData.foodPreference,
        organisationMealTag,
      );
    } else {
      meals = await this.mealRepository.getAllMealsForTimeOfDay(
        timeOfDay,
        mealPlanData.gender,
        mealPlanData.dietGoal,
        mealPlanData.foodPreference,
        organisationMealTag,
      );
    }

    const allIngredients: MealIngredient[] = await this.mealIngredientRepository.getAll();
    const shouldReducePortionSize = !userProfile?.isHighlyActive;
    const returnedMeals = [];
    for (const meal of meals) {
      const mealClone = { ...new Meal(), ...meal };
      if (shouldReducePortionSize) {
        Meal.setLowerCaloricMealQuantities(mealClone, allIngredients);
      }
      Meal.setImageUrls(mealClone);
      Meal.setIngredientDescriptions(mealClone, allIngredients, userAllergens, userProfile?.useImperialUnit);
      const calories = Meal.Calories(mealClone, allIngredients);
      mealClone.calories = calories;
      const fat = Meal.Fat(mealClone, allIngredients, userProfile
        && userProfile.useImperialUnit && !Nutrition.disableImperialOnMacros);
      mealClone.fat = fat;
      const carbs = Meal.Carbs(mealClone, allIngredients, userProfile && userProfile.useImperialUnit
        && !Nutrition.disableImperialOnMacros);
      mealClone.carbs = carbs;
      const protein = Meal.Protein(mealClone, allIngredients, userProfile && userProfile.useImperialUnit
        && !Nutrition.disableImperialOnMacros);
      mealClone.protein = protein;
      returnedMeals.push(mealClone);
    }
    return returnedMeals;
  }

  public async setUserMealPlan(
    mealId: string,
    mealOfDay: string,
    weekDay: string,
    userId: string,
  ): Promise<MealPlanWithMeals> {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }

    const meal = await this.mealRepository.get(mealId);
    if (!meal) {
      throw new HandledError(`No meal found with id ${mealId}`);
    }

    const userMealPlanData: UserMealPlan | undefined = await this.mealPlanRepository.getUserMealPlansForUser(
      userProfile,
      this.getDietGoalFor(userProfile),
    );
    if (userMealPlanData == null) {
      throw Error(`No meal plan found for user with uid ${userId} while trying to set meal`);
    }

    const allMeal: Meal[] = await this.mealRepository.getAll();
    const allIngredients: MealIngredient[] = await this.mealIngredientRepository.getAll();
    const userMealPlan = UserMealPlan.SetMeal(userMealPlanData, meal, DayOfWeek[weekDay], MealOfDay[mealOfDay]);
    const userAllergens = userProfile.allergens ?? [];

    if (userMealPlan.shoppingListExclusions != null && userMealPlan.shoppingListExclusions.length > 0) {
      userMealPlan.shoppingListExclusions = userMealPlan.shoppingListExclusions.filter(
        (excl) => excl.dayOfWeek !== DayOfWeek[weekDay] || excl.timeOfDay !== MealOfDay[mealOfDay],
      );
    }

    const mealPlanData = await this.mealPlanRepository.update(userMealPlan);
    const mealPlanWithMeals = MealPlanWithMeals.Create(
      mealPlanData,
      allMeal,
      allIngredients,
      userAllergens,
      userProfile.isHighlyActive,
      userProfile.useImperialUnit,
    );
    return NutritionSerializer.serializeMealPlanWithMeals(mealPlanWithMeals);
  }

  public async getShoppingList(userId: string): Promise<ShoppingItem[]> {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    const mealPlanWithMeals = await this.getMealPlanForSavedPreferencesNonSerialised(userId);
    const ingredientCountMap = this.countIngredientQuantities(mealPlanWithMeals);
    const allIngredients: MealIngredient[] = await this.mealIngredientRepository.getAll();
    const userAllergens = userProfile?.allergens ?? [];
    let shoppingList = this.populateShoppingList(
      ingredientCountMap,
      allIngredients,
      mealPlanWithMeals.checklistIngredientIds,
      userAllergens,
    );
    if (userProfile && userProfile.useImperialUnit) {
      shoppingList = MetricToImperialConverter.convertShoppingListToImperial(shoppingList);
    }
    const sortedByCategoryList = shoppingList.sort((a, b) => this.compareCategories(a, b));

    const servingCount = mealPlanWithMeals.shoppingListServingCount || 1;
    return sortedByCategoryList.map((s: ShoppingItem) => ({
      ...s,
      quantity: roundFloatToDecimalPlaces(s.quantity * servingCount, 2),
    }));
  }

  public setImageUrls(meal: Meal) {
    Meal.setImageUrls(meal);
  }

  public async captureNutritionRating(userId: string, date: Date, rating: 1 | 2 | 3 | 4 | 5) {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }
    const ratingPercentages = {
      1: 20, 2: 40, 3: 60, 4: 80, 5: 100,
    };
    let existingRatings: NutritionRating[] = [];
    if (userProfile.nutritionRatings) {
      existingRatings = userProfile.nutritionRatings;
    }
    userProfile.nutritionRatings = [
      ...existingRatings, {
        date,
        percent: ratingPercentages[rating],
      }];

    return this.userProfileRepository.update(userProfile);
  }

  public async getLastNutritionRating(userId: string): Promise<NutritionRating | undefined> {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }
    return NutritionRating.getLastRating(userProfile.nutritionRatings);
  }

  public getDietGoalFor(userProfile: UserProfile): DietGoal {
    return userProfile.longTermGoal
      ? this.mapProgramTypeToDietGoal(userProfile.longTermGoal)
      : this.mapProgramTypeToDietGoal(ProgramType.GetStrongAndFitGoal);
  }

  private getAdditionalMealsTag = (userProfile?: UserProfile) => {
    if (!userProfile?.gymName) {
      return;
    }
    const gym = DbGymRepository.getByName(userProfile.gymName);
    const organisation = EnabledFeaturesHydrationService.getGymOrganisation(gym);
    return organisation?.nutritionConfig?.organisationMealTag;
  };

  private compareCategories(a: ShoppingItem, b: ShoppingItem): number {
    const orderOfCategoryA = this.getCategoryOrder(a);
    const orderOfCategoryB = this.getCategoryOrder(b);
    return orderOfCategoryA - orderOfCategoryB;
  }

  private async checkIfRatingRequired(userId: string, currentDate: moment.Moment) {
    let requireRating = true;
    const lastRating = await this.getLastNutritionRating(userId);
    if (lastRating) {
      const lastRatingMoment = moment(lastRating.date);
      requireRating = !currentDate.isSame(lastRatingMoment, 'day');
    }
    return requireRating;
  }

  private getCategoryOrder(shoppingItem: ShoppingItem) {
    const { category } = shoppingItem;
    let orderOfCategory = NutritionConfig.ingredientCategoryOrder.findIndex((c) => category.indexOf(c) !== -1);
    if (orderOfCategory === -1) {
      orderOfCategory = NutritionConfig.maxCategoryOrderNumber;
    }
    return orderOfCategory;
  }

  private populateShoppingList(
    ingredientCountMap: Map<string, number>,
    allIngredients: MealIngredient[],
    checklistIngredientIds: string[],
    allergens: Allergens[],
  ): ShoppingItem[] {
    return Array.from(ingredientCountMap.keys()).map<ShoppingItem>((ingredientId) => {
      const shoppingItem = new ShoppingItem();
      shoppingItem.ingredientId = ingredientId;
      this.setShoppingItemQuantity(ingredientCountMap, ingredientId, shoppingItem);
      shoppingItem.isCheckedFromShoppingList = false;
      this.setShoppingItemIsInChecklist(checklistIngredientIds, ingredientId, shoppingItem);
      this.setShoppingItemIngredientDetails(allIngredients, ingredientId, shoppingItem, allergens);
      return shoppingItem;
    });
  }

  private setShoppingItemIngredientDetails(
    allIngredients: MealIngredient[],
    ingredientId: string,
    shoppingItem: ShoppingItem,
    allergens: Allergens[],
  ) {
    const ingredientDetails = allIngredients.find((i) => i._id === ingredientId);
    if (ingredientDetails) {
      shoppingItem.unit = ingredientDetails.unit;
      shoppingItem.displayDescription = MealIngredient.getDisplayNameOfIngredientOrReplacement(
        ingredientDetails,
        allergens,
      );
      shoppingItem.category = ingredientDetails.category;
      if (shoppingItem.category == null) {
        shoppingItem.category = 'Other';
      }
    }
  }

  private setShoppingItemQuantity(
    ingredientCountMap: Map<string, number>,
    ingredientId: string,
    shoppingItem: ShoppingItem,
  ) {
    const quantity = ingredientCountMap.get(ingredientId);
    if (quantity != null) {
      shoppingItem.quantity = quantity;
    } else {
      shoppingItem.quantity = 0;
    }
  }

  private setShoppingItemIsInChecklist(
    checklistIngredientIds: string[],
    ingredientId: string,
    shoppingItem: ShoppingItem,
  ) {
    shoppingItem.isCheckedFromShoppingList = false;
    if (checklistIngredientIds != null && checklistIngredientIds.length > 0) {
      if (checklistIngredientIds.find((cii) => ingredientId === cii) != null) {
        shoppingItem.isCheckedFromShoppingList = true;
      }
    }
  }

  private countIngredientQuantities(mealPlanWithMeals: MealPlanWithMeals): Map<string, number> {
    const ingredientCountMap = new Map<string, number>();

    for (const day of mealPlanWithMeals.mealSchedule.keys()) {
      const dayPlan = mealPlanWithMeals.mealSchedule.get(day);
      if (dayPlan) {
        for (const mealOfDay of dayPlan.keys()) {
          const meal = dayPlan.get(mealOfDay);
          if (meal && meal.mealIngredients && meal.isInShoppingList === true) {
            for (const ingredientCount of meal.mealIngredients) {
              const oldCountForIngredient = ingredientCountMap.get(ingredientCount.ingredientId);
              if (oldCountForIngredient != null) {
                ingredientCountMap.set(
                  ingredientCount.ingredientId,
                  ingredientCount.quantity + oldCountForIngredient,
                );
              } else {
                ingredientCountMap.set(ingredientCount.ingredientId, ingredientCount.quantity);
              }
            }
          }
        }
      }
    }

    return ingredientCountMap;
  }

  private async getMealPlanForSavedPreferencesNonSerialised(userId: string) {
    const userProfile: UserProfile | undefined = await this.userProfileRepository.get(userId);
    if (!userProfile) {
      throw new HandledError(`No user found with uid ${userId}`);
    }
    const { mealPreferences } = userProfile;
    if (!mealPreferences) {
      throw new HandledError('Meal preference must be set.');
    }

    const { gender } = userProfile;
    let queryGender = gender;
    if (gender === Gender.Other) {
      queryGender = Gender.Female;
    }

    const allMeal: Meal[] = await this.mealRepository.getAll();
    const allIngredients: MealIngredient[] = await this.mealIngredientRepository.getAll();
    const userAllergens = userProfile.allergens ?? [];
    const userMealPlanData: UserMealPlan | undefined = await this.mealPlanRepository.getUserMealPlansForUser(
      userProfile,
      this.getDietGoalFor(userProfile),
    );

    let mealPlanWithMeals;
    if (userMealPlanData) {
      mealPlanWithMeals = MealPlanWithMeals.Create(
        userMealPlanData,
        allMeal,
        allIngredients,
        userAllergens,
        userProfile.isHighlyActive,
        userProfile.useImperialUnit,
      );
    } else { // always ensure we send back the user's own meal plans
      const dietGoal = this.getDietGoalFor(userProfile);
      const { foodPreference } = mealPreferences;
      const organisationMealTag = this.getAdditionalMealsTag(userProfile);
      const mealPlanData: MealPlanData | undefined = await this.mealPlanRepository
        .getMealPlanForSavedPreferencesAndGender(
          dietGoal,
          foodPreference,
          queryGender,
          organisationMealTag,
        );
      const mealPlanComboInWords = `${dietGoal}, ${foodPreference} ${gender} ${organisationMealTag || ''}`;
      if (!mealPlanData) {
        throw new HandledError(`No meal plan found for ${mealPlanComboInWords}`);
      }

      const userMealPlan = UserMealPlan.Create(mealPlanData, userProfile);
      userMealPlan._id = UserMealPlan.GenerateIdFromUserPlan(userMealPlan);
      const newUserMealPlanData = await this.mealPlanRepository.add(userMealPlan);
      mealPlanWithMeals = MealPlanWithMeals.Create(
        newUserMealPlanData,
        allMeal,
        allIngredients,
        userAllergens,
        userProfile.isHighlyActive,
        userProfile.useImperialUnit,
      );
    }
    return mealPlanWithMeals;
  }

  private mapProgramTypeToDietGoal(programType: ProgramType): DietGoal {
    switch (programType) {
      case ProgramType.GainMuscleAndStrengthGoal:
        return DietGoal.BuildMuscle;
      case ProgramType.GetLeanAndFitGoal:
        return DietGoal.BurnFat;
      case ProgramType.GetStrongAndFitGoal:
        return DietGoal.BurnFatAndBuildMuscle;
      case ProgramType.MobilityAndTone:
        return DietGoal.BurnFat;
      case ProgramType.MuscleAndShred:
        return DietGoal.BurnFatAndBuildMuscle;
      default:
        return DietGoal.BurnFatAndBuildMuscle;
    }
  }
}
